<script setup>
import { reactive, computed, watch, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { woocommerceStoresAPI } from 'dashboard/api/webOrder';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
  sections: {
    type: Array,
    default: () => ['shop', 'rules', 'capabilities', 'slots'],
  },
  showSubmit: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits(['submit']);
const DEFAULT_REQUIRED_SLOTS = ['name', 'phone', 'address'];
const BUSINESS_TYPES = ['products', 'services', 'mixed'];

const { t } = useI18n();
const stores = ref([]);

const state = reactive({
  shopName: '',
  businessType: 'products',
  defaultWoocommerceStoreId: '',
  insideCharge: '',
  insideDays: '',
  outsideCharge: '',
  outsideDays: '',
  codEnabled: true,
  advanceAmount: '',
  returnPolicy: '',
  orderStatus: 'pending',
  stockAwareness: true,
  webOrderLookup: true,
  semanticProductSearch: true,
  extraSlots: [],
});

const businessTypeOptions = computed(() =>
  BUSINESS_TYPES.map(value => ({
    value,
    label: t(
      `CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.BUSINESS_TYPE.OPTIONS.${value.toUpperCase()}`
    ),
  }))
);

const orderStatusOptions = computed(() => [
  {
    value: 'pending',
    label: t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.ORDER_STATUS.PENDING'),
  },
  {
    value: 'approved',
    label: t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.ORDER_STATUS.APPROVED'),
  },
]);

const validationRules = {
  shopName: { required, minLength: minLength(1) },
  defaultWoocommerceStoreId: { required },
};

const v$ = useVuelidate(validationRules, state);

const showShop = computed(() => props.sections.includes('shop'));
const showRules = computed(() => props.sections.includes('rules'));
const showCapabilities = computed(() =>
  props.sections.includes('capabilities')
);
const showSlots = computed(() => props.sections.includes('slots'));

const getErrorMessage = field => {
  return v$.value[field].$error ? v$.value[field].$errors[0].$message : '';
};

const formErrors = computed(() => ({
  shopName: getErrorMessage('shopName'),
  defaultWoocommerceStoreId: getErrorMessage('defaultWoocommerceStoreId'),
}));

const selectClass =
  'block w-full reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 h-10 px-3 text-n-slate-12 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand';

const slugifySlotKey = value =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');

const updateStateFromAssistant = assistant => {
  const brain = assistant?.config?.business_brain || {};
  const delivery = brain.delivery || {};
  const sales = assistant?.config?.sales || {};
  state.shopName = brain.shop_name || '';
  state.businessType = BUSINESS_TYPES.includes(brain.business_type)
    ? brain.business_type
    : 'products';
  state.defaultWoocommerceStoreId = brain.default_woocommerce_store_id
    ? String(brain.default_woocommerce_store_id)
    : '';
  state.insideCharge = delivery.inside_charge ?? '';
  state.insideDays = delivery.inside_days ?? '';
  state.outsideCharge = delivery.outside_charge ?? '';
  state.outsideDays = delivery.outside_days ?? '';
  state.codEnabled = brain.cod_enabled !== false;
  state.advanceAmount = brain.advance_amount ?? '';
  state.returnPolicy = brain.return_policy || '';
  state.orderStatus =
    brain.order_status === 'approved' ? 'approved' : 'pending';
  state.stockAwareness = sales.stock_awareness !== false;
  state.webOrderLookup = sales.web_order_lookup !== false;
  state.semanticProductSearch = sales.semantic_product_search !== false;
  state.extraSlots = Array.isArray(brain.extra_slots)
    ? brain.extra_slots.map(slot => ({
        key: slot.key || '',
        label: slot.label || '',
      }))
    : [];
};

const addExtraSlot = () => {
  state.extraSlots.push({ key: '', label: '' });
};

const removeExtraSlot = index => {
  state.extraSlots.splice(index, 1);
};

const handleSubmit = async () => {
  if (showShop.value) {
    const checks = [v$.value.shopName.$validate()];
    if (stores.value.length) {
      checks.push(v$.value.defaultWoocommerceStoreId.$validate());
    }
    const isValid = (await Promise.all(checks)).every(Boolean);
    if (!isValid) return;
  }

  const existingBrain = props.assistant?.config?.business_brain || {};
  const nextBrain = { ...existingBrain };

  if (showShop.value) {
    nextBrain.shop_name = state.shopName;
    nextBrain.business_type = state.businessType;
    if (state.defaultWoocommerceStoreId) {
      nextBrain.default_woocommerce_store_id = Number(
        state.defaultWoocommerceStoreId
      );
    }
  }

  if (showRules.value) {
    nextBrain.delivery = {
      inside_charge:
        state.insideCharge === '' ? null : Number(state.insideCharge),
      inside_days: state.insideDays === '' ? null : Number(state.insideDays),
      outside_charge:
        state.outsideCharge === '' ? null : Number(state.outsideCharge),
      outside_days: state.outsideDays === '' ? null : Number(state.outsideDays),
    };
    nextBrain.cod_enabled = state.codEnabled;
    nextBrain.advance_amount =
      state.advanceAmount === '' ? 0 : Number(state.advanceAmount);
    nextBrain.return_policy = state.returnPolicy;
    nextBrain.order_status = state.orderStatus;
  }

  if (showSlots.value) {
    nextBrain.required_slots = [...DEFAULT_REQUIRED_SLOTS];
    nextBrain.extra_slots = state.extraSlots
      .map(slot => {
        const key = slugifySlotKey(slot.key);
        return {
          key,
          label: (slot.label || '').trim() || key,
        };
      })
      .filter(slot => slot.key && !DEFAULT_REQUIRED_SLOTS.includes(slot.key));
  }

  const payload = {
    config: {
      business_brain: nextBrain,
    },
  };

  if (showCapabilities.value) {
    payload.config.sales = {
      stock_awareness: state.stockAwareness,
      web_order_lookup: state.webOrderLookup,
      semantic_product_search: state.semanticProductSearch,
    };
  }

  emit('submit', payload);
};

const loadStores = async () => {
  const { data } = await woocommerceStoresAPI.list();
  stores.value = Array.isArray(data?.payload) ? data.payload : [];
};

onMounted(loadStores);

defineExpose({ submit: handleSubmit });

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <section
      v-if="showShop"
      class="flex flex-col gap-4 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <h4 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.CARDS.SHOP') }}
      </h4>
      <Input
        v-model="state.shopName"
        :label="t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.SHOP_NAME.LABEL')"
        :placeholder="
          t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.SHOP_NAME.PLACEHOLDER')
        "
        :message="formErrors.shopName"
        :message-type="formErrors.shopName ? 'error' : 'info'"
      />

      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.BUSINESS_TYPE.LABEL') }}
        </label>
        <select v-model="state.businessType" :class="selectClass">
          <option
            v-for="option in businessTypeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <p class="mt-1 mb-0 text-label-small text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.BUSINESS_TYPE.HELP') }}
        </p>
      </div>

      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.STORE.LABEL') }}
        </label>
        <select v-model="state.defaultWoocommerceStoreId" :class="selectClass">
          <option value="" disabled>
            {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.STORE.PLACEHOLDER') }}
          </option>
          <option
            v-for="store in stores"
            :key="store.id"
            :value="String(store.id)"
          >
            {{ store.name }}
          </option>
        </select>
        <p
          v-if="formErrors.defaultWoocommerceStoreId"
          class="mt-1 mb-0 text-label-small text-n-ruby-9"
        >
          {{ formErrors.defaultWoocommerceStoreId }}
        </p>
        <p
          v-else-if="!stores.length"
          class="mt-1 mb-0 text-label-small text-n-slate-11"
        >
          {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.STORE.EMPTY') }}
        </p>
      </div>
    </section>

    <section
      v-if="showRules"
      class="flex flex-col gap-4 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <h4 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.CARDS.RULES') }}
      </h4>
      <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Input
          v-model="state.insideCharge"
          type="number"
          :label="
            t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.INSIDE_CHARGE.LABEL')
          "
          :placeholder="
            t(
              'CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.INSIDE_CHARGE.PLACEHOLDER'
            )
          "
        />
        <Input
          v-model="state.insideDays"
          type="number"
          :label="t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.INSIDE_DAYS.LABEL')"
          :placeholder="
            t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.INSIDE_DAYS.PLACEHOLDER')
          "
        />
        <Input
          v-model="state.outsideCharge"
          type="number"
          :label="
            t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.OUTSIDE_CHARGE.LABEL')
          "
          :placeholder="
            t(
              'CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.OUTSIDE_CHARGE.PLACEHOLDER'
            )
          "
        />
        <Input
          v-model="state.outsideDays"
          type="number"
          :label="
            t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.OUTSIDE_DAYS.LABEL')
          "
          :placeholder="
            t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.OUTSIDE_DAYS.PLACEHOLDER')
          "
        />
      </div>

      <label class="flex items-center justify-between gap-4">
        <span class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.COD.LABEL') }}
        </span>
        <Switch v-model="state.codEnabled" />
      </label>

      <Input
        v-model="state.advanceAmount"
        type="number"
        :label="t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.ADVANCE.LABEL')"
        :placeholder="
          t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.ADVANCE.PLACEHOLDER')
        "
      />

      <TextArea
        v-model="state.returnPolicy"
        :label="t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.RETURN_POLICY.LABEL')"
        :placeholder="
          t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.RETURN_POLICY.PLACEHOLDER')
        "
        :max-length="2000"
        auto-height
      />

      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.ORDER_STATUS.LABEL') }}
        </label>
        <select v-model="state.orderStatus" :class="selectClass">
          <option
            v-for="option in orderStatusOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <p class="mt-1 mb-0 text-label-small text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.ORDER_STATUS.HELP') }}
        </p>
      </div>
    </section>

    <section
      v-if="showCapabilities"
      class="flex flex-col gap-4 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <h4 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.CARDS.CAPABILITIES') }}
      </h4>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.DATA_ACCESS.TITLE') }}
      </p>
      <div class="flex flex-col gap-1">
        <label class="flex items-center justify-between gap-4">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.DATA_ACCESS.STOCK') }}
          </span>
          <Switch v-model="state.stockAwareness" />
        </label>
        <p class="mb-0 text-label-small text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.DATA_ACCESS.STOCK_HELP') }}
        </p>
      </div>
      <div class="flex flex-col gap-1">
        <label class="flex items-center justify-between gap-4">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.DATA_ACCESS.ORDER_LOOKUP') }}
          </span>
          <Switch v-model="state.webOrderLookup" />
        </label>
        <p class="mb-0 text-label-small text-n-slate-11">
          {{
            t('CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.DATA_ACCESS.ORDER_LOOKUP_HELP')
          }}
        </p>
      </div>
      <div class="flex flex-col gap-1">
        <label class="flex items-center justify-between gap-4">
          <span class="text-sm text-n-slate-12">
            {{
              t(
                'CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.DATA_ACCESS.SEMANTIC_PRODUCT_SEARCH'
              )
            }}
          </span>
          <Switch v-model="state.semanticProductSearch" />
        </label>
        <p class="mb-0 text-label-small text-n-slate-11">
          {{
            t(
              'CAPTAIN.ASSISTANTS.FORM.BEHAVIOR.DATA_ACCESS.SEMANTIC_PRODUCT_SEARCH_HELP'
            )
          }}
        </p>
      </div>
    </section>

    <section
      v-if="showSlots"
      class="flex flex-col gap-4 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <h4 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.CARDS.SLOTS') }}
      </h4>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.SLOTS.HELP') }}
      </p>
      <div class="flex flex-wrap gap-2">
        <span
          v-for="slot in DEFAULT_REQUIRED_SLOTS"
          :key="slot"
          class="rounded-full bg-n-alpha-black2 px-3 py-1 text-sm text-n-slate-12"
        >
          {{
            t(
              `CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.SLOTS.${slot.toUpperCase()}`
            )
          }}
        </span>
      </div>
      <p class="mb-0 text-label-small text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.SLOTS.LOCKED_NOTE') }}
      </p>
      <div class="flex items-center justify-between gap-4">
        <h5 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.SLOTS.EXTRAS.TITLE') }}
        </h5>
        <Button
          ghost
          xs
          :label="t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.SLOTS.EXTRAS.ADD')"
          @click="addExtraSlot"
        />
      </div>
      <div
        v-for="(slot, index) in state.extraSlots"
        :key="index"
        class="grid grid-cols-1 gap-4 md:grid-cols-[1fr_1fr_auto]"
      >
        <Input
          v-model="slot.key"
          :label="t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.SLOTS.EXTRAS.KEY')"
          :placeholder="
            t(
              'CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.SLOTS.EXTRAS.KEY_PLACEHOLDER'
            )
          "
        />
        <Input
          v-model="slot.label"
          :label="
            t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.SLOTS.EXTRAS.LABEL')
          "
          :placeholder="
            t(
              'CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.SLOTS.EXTRAS.LABEL_PLACEHOLDER'
            )
          "
        />
        <div class="flex items-end">
          <Button
            ghost
            ruby
            xs
            :label="
              t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_BRAIN.SLOTS.EXTRAS.REMOVE')
            "
            @click="removeExtraSlot(index)"
          />
        </div>
      </div>
    </section>

    <div v-if="showSubmit">
      <Button
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
